<script setup lang="ts">
import { House, Plus, Settings, ShieldCheck } from "lucide-vue-next";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { apiClient } from "@/api/client";
import type { SubjectProgress } from "@/api/types";
import AddSubjectPanel from "@/components/AddSubjectPanel.vue";
import AppFooter from "@/components/AppFooter.vue";
import StartPlanPanel from "@/components/StartPlanPanel.vue";
import SendFeedbackButton from "@/components/feedback/SendFeedbackButton.vue";
import SessionOverlay from "@/components/session/SessionOverlay.vue";
import AttainmentRing from "@/components/ui/AttainmentRing.vue";
import RlSkeleton from "@/components/ui/RlSkeleton.vue";
import { subjectTierFor, type AttainmentTier } from "@/lib/attainment";
import { isPayerWithoutSeat } from "@/lib/gettingStarted";
import { useAuthStore } from "@/stores/auth";
import { useHomeStore } from "@/stores/home";

/**
 * Authenticated shell (PRD §6.1 PE-1.1).
 *
 * Subjects are first-class nav items grouped by level — there is no index
 * page between the sidebar and a subject. A student may carry ~10 GCSEs, so
 * the subject region scrolls on its own and the identity/logout foot stays
 * pinned rather than being pushed below the fold.
 */

const auth = useAuthStore();
const home = useHomeStore();
const route = useRoute();
const router = useRouter();

/** GCSE before A-level; anything the ingest produces beyond those sorts last. */
const LEVEL_ORDER = ["GCSE", "A-level"];

interface NavSubject {
  id: string;
  name: string;
  pct: number;
  tier: AttainmentTier;
}

interface NavGroup {
  level: string;
  items: NavSubject[];
}

function toNavSubject(s: SubjectProgress): NavSubject {
  // `knowledge_pct` and `attainment_counts` come from /me/home (§7 D6). The
  // fallbacks keep the ring honest rather than inventing a number when an
  // older API build answers.
  const pct = s.knowledge_pct ?? 0;
  return {
    id: s.subject_id,
    name: s.title,
    pct,
    tier: subjectTierFor(pct, s.attainment_counts),
  };
}

const navGroups = computed<NavGroup[]>(() => {
  const byLevel = new Map<string, NavSubject[]>();
  for (const s of home.subjects) {
    const level = s.level || "Other";
    const bucket = byLevel.get(level);
    if (bucket) bucket.push(toNavSubject(s));
    else byLevel.set(level, [toNavSubject(s)]);
  }

  const rank = (level: string): number => {
    const i = LEVEL_ORDER.indexOf(level);
    return i === -1 ? LEVEL_ORDER.length : i;
  };

  return [...byLevel.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([level, items]) => ({
      level,
      items: items.sort((a, b) => a.name.localeCompare(b.name)),
    }));
});

const showSubjectSkeleton = computed(() => home.loading && !home.loaded);

/**
 * Add subject lives at `/?add=1` (batch contract). Existing query params are
 * carried over so the slide-over layers onto whatever the student was
 * looking at instead of resetting it.
 */
const addSubjectTarget = computed(() => ({
  path: "/",
  query: { ...route.query, add: "1" },
}));

/**
 * D8 (rl-757.50): a payer who holds no entitling seat of their own gets no
 * "Add subject" item, because the item can only reach the paywall. The
 * capability is not withdrawn — §4.4 puts "Start a plan for yourself" at the
 * foot of their Today — so what goes is a control that cannot work, not a
 * route out.
 *
 * Both halves are load-bearing, and neither is the `payer` role claim: an
 * account can fund a seat AND hold one, and that person is a learner who
 * happens to pay. The rule lives in `isPayerWithoutSeat`, shared with Today's
 * role branch and the getting-started card's state machine, because it reads
 * two endpoints and three spellings of it would drift silently.
 *
 * The starting pair is the permissive one: zero seats and an assumed seat of
 * one's own. An unresolved probe must not make a learner's nav vanish. Only
 * the live subscription capability authorizes a seats read; token claims can
 * lag a first purchase, and admin access does not imply payer access.
 */
const paidSeatCount = ref(0);
const hasEntitlingSeat = ref(true);

const payerWithNoSeat = computed(() =>
  isPayerWithoutSeat({
    paidSeatCount: paidSeatCount.value,
    hasEntitlingSeat: hasEntitlingSeat.value,
  }),
);

async function loadPayerState(): Promise<void> {
  try {
    const sub = await apiClient.getSubscription();
    hasEntitlingSeat.value = sub.status !== null && sub.status !== "none";
    paidSeatCount.value = sub.can_manage_seats
      ? (await apiClient.getPayerSeats()).length
      : 0;
  } catch {
    // Keep the learner navigation available when either read is unresolved.
  }
}

onMounted(() => {
  home.load();
  void loadPayerState();
});

// The shell outlives a logout/login within one page load, so without this a
// stale subject list — and a stale answer to D8's payer question — would leak
// from one account into the next.
watch(
  () => auth.isAuthenticated,
  (isAuthed) => {
    home.reset();
    paidSeatCount.value = 0;
    hasEntitlingSeat.value = true;
    if (isAuthed) {
      home.load();
      void loadPayerState();
    }
  },
);

async function handleLogout(): Promise<void> {
  await auth.logout();
  home.reset();
  await router.push("/");
}
</script>

<template>
  <div class="app-shell">
    <aside class="sidebar">
      <RouterLink to="/" class="sidebar-brand">
        <img src="@/rl_logo.svg" alt="" class="sidebar-logo" />
        <span>RevisionLab</span>
      </RouterLink>

      <!--
        The scrolling region. `min-height: 0` is load-bearing: without it a
        flex child refuses to shrink below its content, and the pinned foot
        gets pushed off-screen once a student carries ~10 subjects.
      -->
      <nav class="sidebar-scroll" aria-label="Main">
        <RouterLink
          to="/"
          class="nav-item"
          active-class="nav-item-active"
          exact-active-class="nav-item-active"
        >
          <House class="nav-icon" aria-hidden="true" />
          <span class="nav-label">Today</span>
        </RouterLink>

        <template v-if="showSubjectSkeleton">
          <div class="nav-section">Subjects</div>
          <div v-for="n in 3" :key="n" class="nav-skeleton">
            <RlSkeleton width="18px" :height="18" />
            <RlSkeleton width="60%" :height="12" />
          </div>
        </template>

        <template v-else>
          <div v-for="group in navGroups" :key="group.level">
            <div class="nav-section">{{ group.level }}</div>
            <RouterLink
              v-for="subject in group.items"
              :key="subject.id"
              :to="`/subjects/${subject.id}`"
              class="nav-item nav-subject"
              active-class="nav-item-active"
            >
              <AttainmentRing
                :pct="subject.pct"
                :tier="subject.tier"
                :size="18"
              />
              <span class="nav-label">{{ subject.name }}</span>
            </RouterLink>
          </div>
        </template>

        <RouterLink
          v-if="!payerWithNoSeat"
          :to="addSubjectTarget"
          class="nav-item nav-item-quiet"
        >
          <Plus class="nav-icon" aria-hidden="true" />
          <span class="nav-label">Add subject</span>
        </RouterLink>

        <div class="nav-section">Settings</div>
        <RouterLink
          to="/account"
          class="nav-item"
          active-class="nav-item-active"
        >
          <Settings class="nav-icon" aria-hidden="true" />
          <span class="nav-label">Account</span>
        </RouterLink>
        <RouterLink
          v-if="auth.hasPermission('admin.catalogue.read')"
          to="/admin"
          class="nav-item"
          active-class="nav-item-active"
        >
          <ShieldCheck class="nav-icon" aria-hidden="true" />
          <span class="nav-label">Admin</span>
        </RouterLink>
      </nav>

      <div class="sidebar-foot">
        <span class="sidebar-user-email">{{ auth.user?.email }}</span>
        <button class="sidebar-logout" @click="handleLogout">Log out</button>
      </div>
    </aside>

    <main class="shell-main">
      <div class="shell-content">
        <slot />
      </div>
      <AppFooter />
    </main>

    <!--
      Add-subject slide-over (PE-5.1). Self-managing: it reads `?add=1` off
      the route itself and renders nothing otherwise. Mounted at shell level
      so the sidebar button works from any authenticated page.
    -->
    <AddSubjectPanel />

    <!--
      Start-your-plan slide-over (§4.2, rl-757.47). Self-managing in the same
      way, off `?plan=1`, and mounted here so the getting-started card, a
      recovery notice and a bookmark all open the one panel. §8 Q3 keeps it
      apart from `?add=1` rather than branching that panel on a paywall.
    -->
    <StartPlanPanel />

    <!--
      Session overlay (PE-8.1). A session is a verb, never a destination, so it
      has no route: like the slide-over above it reads `?session=1` off the
      current `/subjects/:subjectId` route itself and renders nothing otherwise.
      Mounted here so a launch from Today, a subject or a topic all land in the
      same place.
    -->
    <SessionOverlay />

    <!--
      tutor-uox.2.a — Global "Send feedback" FAB. Mounted at the shell
      level so it's visible on every authenticated page; position
      fixed bottom-right, z-index above the sidebar.
    -->
    <SendFeedbackButton />
  </div>
</template>

<style scoped>
.app-shell {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  width: var(--rl-sidebar-width);
  min-width: var(--rl-sidebar-width);
  background: var(--rl-bg-subtle);
  border-right: 1px solid var(--rl-border-subtle);
  display: flex;
  flex-direction: column;
  padding: var(--sp-5) 0 var(--sp-4);
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 100;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: 0 var(--sp-5) var(--sp-5);
  text-decoration: none;
  color: var(--rl-fg-1);
  font-family: var(--rl-font-sans);
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.01em;
  flex: none;
}

.sidebar-brand:hover {
  text-decoration: none;
  color: var(--rl-fg-1);
}

.sidebar-logo {
  width: 20px;
  height: 20px;
}

.sidebar-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 0 var(--sp-3);
}

.nav-section {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--rl-fg-3);
  padding: var(--sp-4) var(--sp-3) var(--sp-1);
}

.nav-item {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 0;
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--radius-sm);
  color: var(--rl-fg-2);
  text-decoration: none;
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-md);
  font-weight: 500;
  letter-spacing: -0.005em;
  transition:
    color var(--t-fast),
    background-color var(--t-fast);
}

.nav-item:hover {
  color: var(--rl-fg-1);
  background: var(--rl-bg-muted);
  text-decoration: none;
}

.nav-item-active {
  color: var(--rl-fg-1);
  background: var(--rl-bg-muted);
  font-weight: 600;
}

.nav-item-quiet {
  color: var(--rl-fg-3);
  margin-top: var(--sp-3);
}

.nav-icon {
  width: 16px;
  height: 16px;
  flex: none;
  stroke-width: 1.75;
}

.nav-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-skeleton {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
}

.sidebar-foot {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  padding: var(--sp-4) var(--sp-3) 0;
  margin: 0 var(--sp-5);
  border-top: 1px solid var(--rl-border-subtle);
}

.sidebar-user-email {
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-logout {
  background: none;
  border: none;
  color: var(--rl-fg-3);
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-sm);
  font-weight: 500;
  padding: 0;
  cursor: pointer;
  text-align: left;
  transition: color var(--t-fast);
}

.sidebar-logout:hover {
  color: var(--rl-fg-1);
}

.shell-main {
  margin-left: var(--rl-sidebar-width);
  flex: 1;
  min-width: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: var(--sp-8);
}

/* Keeps the footer at the bottom of short pages instead of mid-screen. */
.shell-content {
  flex: 1;
  min-width: 0;
}

/* The fixed sidebar stays in view during a session, so the session fills the
   space beside it. Covering the whole viewport would centre the plan under
   the sidebar and hide its left edge on a 768px tablet. */
.app-shell > :deep(.overlay) {
  left: var(--rl-sidebar-width);
}

@media (max-width: 767px) {
  .app-shell {
    flex-direction: column;
  }

  /* The stacking order only matters beside a fixed desktop sidebar. Left at
     100 here, a flex item still stacks, so the static top block would paint
     over the session overlay and the slide-over scrims (GitHub #378). */
  .sidebar {
    position: static;
    z-index: auto;
    width: 100%;
    min-width: 0;
    border-right: 0;
    border-bottom: 1px solid var(--rl-border-subtle);
    padding: var(--sp-4);
    gap: var(--sp-3);
  }

  .sidebar-brand {
    padding: 0;
  }

  .sidebar-scroll {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    overflow: visible;
    gap: var(--sp-1);
    padding: 0;
  }

  .sidebar-scroll > div {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    min-width: 0;
    max-width: 100%;
  }

  .nav-section {
    padding: var(--sp-2) var(--sp-3);
  }

  .nav-item {
    min-height: 44px;
    max-width: 100%;
  }

  .nav-item-quiet {
    margin-top: 0;
  }

  .sidebar-foot {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-3);
    margin: 0;
    padding: var(--sp-2) 0 0;
  }

  .sidebar-user-email {
    min-width: 0;
  }

  .sidebar-logout {
    flex-shrink: 0;
    min-height: 40px;
  }

  .shell-main {
    width: 100%;
    max-width: none;
    min-height: 0;
    margin: 0;
    padding: var(--sp-4);
  }

  /* GitHub #378: on a phone the session sits in the page directly under the
     RevisionLab/Today block instead of floating over a blurred page that
     scrolls separately. The page it was launched from is hidden meanwhile,
     so the session is the only thing to scroll. It keeps its stacking order
     so its own fixed layers (discard confirm, idle nudge) stay on top. */
  .app-shell:has(> .overlay) > .shell-main {
    display: none;
  }

  .app-shell > :deep(.overlay) {
    position: relative;
    inset: auto;
    flex: 1;
    overflow: visible;
    background: none;
    backdrop-filter: none;
  }
}
</style>
